// Conteos centralizados de los badges del Home y dashboards. Todos aplican el corte
// FECHA_CORTE (1-ene-2026): las facturas/movimientos históricos pre-Clay no inflan los
// badges. Las facturas recibidas excluyen NCs (tipo_dte=61): reducen deuda, no son DTEs
// pendientes pagables. La regla vive acá una sola vez — consumidores: Admin Home y
// DashboardFinanciero.
import {
  CLASIFICACION,
  TIPO_DOCUMENTO,
  derivarClasificacion,
  normalizarExterna,
  normalizarHonorario,
  normalizarSiiRecibida,
} from "./compras/documentosCosto";
import { FECHA_CORTE } from "./estadoCuenta/saldoInicial";
import { client } from "./supabase";

// Facturas SII recibidas pendientes desde el corte, excluyendo NCs (tipo_dte=61).
export async function getCountRecibidasPendientes(pais) {
  const { count } = await client
    .from("facturas_sii_recibidas")
    .select("id", { count: "exact", head: true })
    .eq("pais", pais)
    .eq("estado_vinculacion", "pendiente")
    .gte("fecha_emision", FECHA_CORTE)
    .neq("tipo_dte", 61);
  return count || 0;
}

// Facturas de COMPRA SIN CLASIFICAR (eje CONTABLE) — el mismo número que la card "SIN CLASIFICAR"
// de ComprasTab. Reproduce EXACTO su universo: las 3 fuentes (SII recibidas + externas + honorarios
// que computan) con los MISMOS filtros de `useDocumentosCosto` (país, corte FECHA_CORTE en sii/
// honorarios, externas no anuladas), normalizadas con las MISMAS funciones puras y pasadas por el
// MISMO `derivarClasificacion`; se excluyen las NC (no se clasifican). Se reusan las funciones de
// `documentosCosto` a propósito: un solo criterio, sin riesgo de divergir del número de la card.
export async function getCountComprasSinClasificar(pais) {
  const [sii, externas, honorarios] = await Promise.all([
    client
      .from("facturas_sii_recibidas")
      .select("id, tipo_dte, estado_vinculacion, tipo_contable, cuenta_er, fecha_emision")
      .eq("pais", pais)
      .gte("fecha_emision", FECHA_CORTE),
    client.from("facturas_externas").select("id, estado, cuenta_er").eq("pais", pais).neq("estado", "anulada"),
    client
      .from("boletas_honorarios")
      .select("id, computa, tipo_contable, cuenta_er, fecha_emision")
      .eq("pais", pais)
      .gte("fecha_emision", FECHA_CORTE),
  ]);

  const docs = [
    ...(sii.data || []).map((f) => normalizarSiiRecibida(f)),
    ...(externas.data || []).map(normalizarExterna),
    ...(honorarios.data || []).filter((b) => b.computa).map(normalizarHonorario),
  ];

  return docs.filter(
    (d) => d.tipo !== TIPO_DOCUMENTO.NOTA_CREDITO && derivarClasificacion(d).estado === CLASIFICACION.SIN_CLASIFICAR
  ).length;
}

// Movimientos bancarios SIN VINCULAR desde el corte: sin fila en el puente `movimiento_bancario_facturas`
// (MBF), sin ser excedente de una operación de factoring, sin asiento de caja, y no ignorados. Un
// movimiento se cuenta pendiente solo si NINGUNA superficie de vínculo lo cubre.
//
// B3 Fase A retiró las guardas de FK directa (`factura_sii_emitida_id`/`_recibida_id`): esas columnas
// dejaron de ser fuente de vínculo. En su lugar el vínculo se reconoce por el puente MBF y por el
// excedente de factoring (`operaciones_factoring.movimiento_bancario_remanente_id`) — el remanente que
// devuelve el factor NO es un movimiento por vincular; su factura ya se cobró por factoring (10.193).
export async function getCountMovimientosPendientes(pais) {
  // Anti-set: movimientos vinculados por alguna superficie no-FK. Ambas tablas son chicas (cientos),
  // sin columna `pais`; se traen SOLO los ids con `.range` (10.167). El anti-join (NOT IN) lo ejecuta
  // el server: los movimientos NO viajan al cliente.
  const [mbfRes, factRes] = await Promise.all([
    client.from("movimiento_bancario_facturas").select("movimiento_bancario_id").range(0, 49999),
    client.from("operaciones_factoring").select("movimiento_bancario_remanente_id").range(0, 49999),
  ]);
  if (mbfRes.error) throw mbfRes.error;
  if (factRes.error) throw factRes.error;
  const idsVinculados = [
    ...new Set(
      [
        ...(mbfRes.data || []).map((r) => r.movimiento_bancario_id),
        ...(factRes.data || []).map((r) => r.movimiento_bancario_remanente_id),
      ].filter((id) => id != null)
    ),
  ];

  let query = client
    .from("movimientos_bancarios")
    .select("id", { count: "exact", head: true })
    .eq("pais", pais)
    .gte("fecha", FECHA_CORTE)
    .is("movimiento_caja_id", null)
    .neq("estado", "ignorado");
  if (idsVinculados.length > 0) query = query.not("id", "in", `(${idsVinculados.join(",")})`);

  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
}

// Movimientos bancarios vinculados a pagos de empleados (se restan del badge de Caja).
export async function getCountPagosEmpleadoMovs(pais) {
  const { count } = await client
    .from("movimientos_bancarios")
    .select("id, pagos_empleados!inner(id)", { count: "exact", head: true })
    .eq("pais", pais);
  return count || 0;
}

// Badge compuesto "Caja": movimientos pendientes + recibidas pendientes − pagos a empleado.
export async function getCountCajaPendientes(pais) {
  const [movs, recibidas, pagos] = await Promise.all([
    getCountMovimientosPendientes(pais),
    getCountRecibidasPendientes(pais),
    getCountPagosEmpleadoMovs(pais),
  ]);
  return movs + recibidas - pagos;
}
